import { featureCollection } from '@turf/helpers';
import { LngLatBounds } from 'mapbox-gl';

import { FieldsFeatureCollection } from '../types/fields';

const BBOX_COORD_PRECISION = 4;

function formatBboxCoords(
  west: number,
  south: number,
  east: number,
  north: number,
): string {
  return [west, south, east, north]
    .map((coord) => coord.toFixed(BBOX_COORD_PRECISION))
    .join(',');
}

/** Bbox string from a PostGIS / plotmaxbounds extent `[west, south, east, north]`. */
export function serializeExtentBbox(
  extent: [number, number, number, number],
): string {
  const [west, south, east, north] = extent;
  return formatBboxCoords(west, south, east, north);
}

/** Stable bbox string for the visible viewport (~10 m precision). */
export function serializeMapBounds(bounds: LngLatBounds): string {
  const [west, south, east, north] = bounds.toArray().flat();
  return formatBboxCoords(west, south, east, north);
}

function sameFeatureIds(
  left: FieldsFeatureCollection['features'],
  right: FieldsFeatureCollection['features'],
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (feature, index) =>
        feature.id === right[index].id &&
        feature.properties?.color === right[index].properties?.color,
    )
  );
}

interface SelectPlotmapFeaturesArgs {
  source: FieldsFeatureCollection | undefined;
  filterIds?: string[];
  hideFilteredIds?: boolean;
  lastRendered: FieldsFeatureCollection;
}

/** Keep a stable GeoJSON reference for Mapbox. */
export function selectPlotmapFeatures({
  source,
  filterIds = [],
  hideFilteredIds = false,
  lastRendered,
}: SelectPlotmapFeaturesArgs): FieldsFeatureCollection {
  if (!source) {
    return lastRendered;
  }

  let features = source.features;

  if (hideFilteredIds && filterIds.length > 0) {
    features = features.filter(
      (feature) => !feature.id || !filterIds.includes(feature.id.toString()),
    );
  }

  if (features.length === 0) {
    return featureCollection([]) as FieldsFeatureCollection;
  }

  if (sameFeatureIds(features, lastRendered.features)) {
    return lastRendered;
  }

  return featureCollection(features) as FieldsFeatureCollection;
}
